/**
 * Utility functions for formatting numbers and currency
 */

/**
 * Format a number with commas (no currency symbol, no decimal places)
 * Example: 1200.00 -> "1,200"
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted number with commas
 */
export const formatNumber = (amount) => {
  if (!amount && amount !== 0) return '0';
  
  try {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0';
    
    // Format with commas, no decimal places
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  } catch (error) {
    console.error('Error formatting number:', error);
    return '0';
  }
};

/**
 * Format currency with commas (no currency symbol, no decimal places)
 * This is an alias for formatNumber for backward compatibility
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted number with commas
 */
export const formatCurrency = formatNumber;

/**
 * Format a number with commas and decimal places (for cases where decimals are needed)
 * @param {number|string} amount - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number with commas and decimals
 */
export const formatNumberWithDecimals = (amount, decimals = 2) => {
  if (!amount && amount !== 0) return '0.00';
  
  try {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0.00';
    
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  } catch (error) {
    console.error('Error formatting number with decimals:', error);
    return '0.00';
  }
};
